import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import { AgeGroup } from '../../types/domain';
import {
  GlucoseZone,
  MeasurementContextGroup,
  getGlucoseZone,
  getGlucoseStatusText,
  getMeasurementContextsByGroup,
  measurementContextGroupLabels,
  thresholdsFor
} from '../../lib/domain-data';
import { GlucoseTargets } from '../../types/domain';

export default function CheckGlucose() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(null);
  const [glucoseValue, setGlucoseValue] = useState<number | null>(100);
  const [measurementContext, setMeasurementContext] = useState<string | null>(null);
  const [targets, setTargets] = useState<GlucoseTargets | null>(null);
  const [completionMessage, setCompletionMessage] = useState<string | null>(null);
  const [injectionDate, setInjectionDate] = useState<string>('');
  const [injectionTime, setInjectionTime] = useState<string>('');
  const [isSavingInjection, setIsSavingInjection] = useState(false);
  const [saveInjectionError, setSaveInjectionError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      return;
    }
    let cancelled = false;
    // Falls back to the cached copy and then to the seeded defaults, so a
    // reading is still coloured offline (FR-06).
    dataService.getEffectiveTargets(user.id).then(loaded => {
      if (!cancelled) {
        setTargets(loaded);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    setInjectionDate(local.toISOString().slice(0, 10));
    setInjectionTime(local.toISOString().slice(11, 16));
  }, []);

  useEffect(() => {
    if (profile?.birth_date) {
      const birthDate = new Date(profile.birth_date);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      const calculatedGroup: AgeGroup = age < 11 ? '2-10' : '11-plus';
      if (ageGroup !== calculatedGroup) {
        setAgeGroup(calculatedGroup);
      }
    }
  }, [profile, ageGroup]);

  const handleSelectMeasurementContext = (id: string) => {
    setMeasurementContext(id);
  };

  const handleGlucoseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setGlucoseValue(!value || value <= 0 ? null : value);
  };

  const handleGlucoseNext = (needsInjection: boolean) => {
    if (needsInjection) {
      // InjectInsulin writes the row on this path, so the measurement context
      // has to travel with the value or it is lost (DF-03).
      navigate('/inject-insulin', { state: { glucoseValue, measurementContext } });
    } else {
      navigate('/');
    }
  };

  const handleSaveGlucoseOnly = async () => {
    if (!ageGroup || !user || glucoseValue === null || !measurementContext) {
      return;
    }

    setIsSavingInjection(true);
    setSaveInjectionError(null);
    try {
      await dataService.addInjection(user.id, {
        date: injectionDate,
        time: injectionTime,
        siteId: null,
        insulinTypeId: null,
        ageGroup,
        glucoseValue: glucoseValue,
        measurementContext
      });

      setCompletionMessage('Data gula darah berhasil disimpan.');
      setTimeout(() => navigate('/'), 2000);
    } catch {
      setSaveInjectionError('Terjadi kesalahan saat menyimpan data gula darah.');
    } finally {
      setIsSavingInjection(false);
    }
  };

  // Derived rather than stored: the zone depends on the value, the chosen
  // measurement context (pre- vs post-prandial) and the configured targets,
  // so it has to recompute when any of the three changes.
  const glucoseZone: GlucoseZone | null = getGlucoseZone(
    glucoseValue,
    thresholdsFor(targets, measurementContext)
  );

  const renderGlucoseStatus = () => {
    const glucoseStatusText = getGlucoseStatusText(
      glucoseZone,
      targets?.messages
    );
    if (!glucoseZone || glucoseValue === null || !glucoseStatusText) {
      return null;
    }
    return (
      <div className={`glucose-status ${glucoseZone}`}>
        <div>{glucoseStatusText}</div>
        <div>{glucoseValue} mg/dL</div>
      </div>
    );
  };

  const renderContextGroup = (group: MeasurementContextGroup) => (
    <div key={group} className="mb-3">
      <div className="context-group-label">{measurementContextGroupLabels[group]}</div>
      <div className="option-grid">
        {getMeasurementContextsByGroup(group).map(context => (
          <button
            key={context.id}
            type="button"
            className={`chip chip-compact ${
              measurementContext === context.id ? 'selected' : ''
            }`}
            aria-pressed={measurementContext === context.id}
            onClick={() => handleSelectMeasurementContext(context.id)}
          >
            {context.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (ageGroup === null) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-xl text-gray-600">Menyiapkan data adik...</div>
      </div>
    );
  }

  return (
    <div className="screen min-h-[100dvh] h-full">
      <div
        className="screen-card"
        role="group"
        aria-labelledby="glucose-step-title"
      >
        <div id="glucose-step-title" className="section-title">
          Cek gula darah
        </div>
        <div className="steps">
          <div className="step-pill active">1. Waktu cek</div>
          <div className="step-pill">2. Angka gula</div>
          <div className="step-pill">3. Lanjut?</div>
        </div>

        <div className="subtitle">1. Pilih waktu pemeriksaan</div>
        {(['pre', 'post', 'other'] as MeasurementContextGroup[]).map(renderContextGroup)}

        <div className="subtitle">2. Masukkan hasil gula darah</div>
        <div className="flex flex-col items-center">
          <input
            type="number"
            inputMode="numeric"
            className="value-display w-40 bg-transparent border-b-2 border-slate-200 focus:border-indigo-500 outline-none transition-colors p-0 appearance-none m-0"
            value={glucoseValue ?? ''}
            placeholder="---"
            onChange={handleGlucoseChange}
            aria-label="Masukkan nilai gula darah"
          />
        </div>
        <div className="value-label">mg/dL</div>
        <input
          type="range"
          min={40}
          max={400}
          step={1}
          value={glucoseValue ?? 40}
          onChange={handleGlucoseChange}
          aria-label="Geser untuk mengatur nilai gula darah"
        />

        {renderGlucoseStatus()}

        <div className="subtitle mt-2">
          3. Perlu lanjut ke suntik insulin?
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-2 col-span-2 md:col-span-1 md:contents">
            <button
              type="button"
              className="btn-secondary w-full md:w-auto bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:text-emerald-800"
              disabled={isSavingInjection || glucoseValue === null || !measurementContext}
              onClick={handleSaveGlucoseOnly}
              aria-label="Hanya simpan cek darah saja"
            >
              {isSavingInjection ? 'Menyimpan...' : 'Hanya simpan cek darah saja'}
            </button>
            <button
              type="button"
              className="btn-primary w-full md:w-auto"
              disabled={glucoseValue !== null && !measurementContext}
              onClick={() => handleGlucoseNext(true)}
              aria-label="Lanjut ke langkah suntik insulin"
            >
              Lanjut suntik
            </button>
            <button
              type="button"
              className="btn-secondary w-full md:w-auto"
              onClick={() => handleGlucoseNext(false)}
              aria-label="Tidak lanjut suntik insulin sekarang"
            >
              Batal
            </button>
          </div>
        </div>
        {glucoseValue !== null && !measurementContext ? (
          <div className="subtitle mt-2 text-center">
            Pilih dulu waktu pemeriksaan ya.
          </div>
        ) : null}
        {saveInjectionError ? (
          <div className="subtitle mt-2 text-red-700 text-center">
            {saveInjectionError}
          </div>
        ) : null}
        {completionMessage ? (
          <div className="completion-message mt-2">{completionMessage}</div>
        ) : null}
      </div>
    </div>
  );
}
